// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useQuery } from '@apollo/client';
import { PLANTING_STATE, WEIGHT_UNITS, formatTime, isPlantStep, utils } from '@seedplanner/shared-utils';
import Decimal from 'decimal.js';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router';
import { ExpandoCard, TitleCard } from '../components/common/card';
import { PageError } from '../components/common/error';
import { Time } from '../components/common/time';
import PageView from '../components/common/view/PageView';
import { CropIdLink } from '../components/crop';
import HarvestLink from '../components/harvest/HarvestLink';
import LostTraysTable from '../components/lostTrays/LostTraysTable';
import { OrderTable } from '../components/order';
import { compareOrders } from '../components/order/compareOrders';
import { PlantingTable } from '../components/planting';
import PlantingGroupOptions from '../components/plantingGroup/PlantingGroupOptions';
import { getPlantingStage } from '../components/plantingGroup/getPlantingStage';
import { TaskGroupTable } from '../components/task';
import { groupTasksWithStatus } from '../components/task/groupTasks';
import { useUserContext } from '../context/UserContext';
import { Box } from '../lib/base';
import { PLANTING_GROUP_QUERY } from '../queries/PlantingGroupViewQuery';
import * as routes from '../routes';
import type { PlantingGroupQuery, PlantingGroupQueryVariables } from '../types/__generated__/graphql';
import { createID, formatStateOrStatus, getID, getNumericID, shortenWeightUnit } from '../utils';
import { formatNumber } from '../utils/formatNumber';

export default function PlantingGroupView() {
  const { plantingId } = useParams<{ readonly plantingId: string }>();
  const { timezone, language } = useUserContext();
  if (plantingId === undefined) {
    return <PageError />;
  }
  const { data, loading, error, refetch } = useQuery<PlantingGroupQuery, PlantingGroupQueryVariables>(
    PLANTING_GROUP_QUERY,
    {
      variables: { id: createID.planting(plantingId) },
    },
  );

  if (error) {
    return <PageError error={error} />;
  }
  if (loading || data?.plantingGroup == null) {
    return (
      <PageView
        title="Planting Group"
        name="Planting Group"
        pluralName="Harvests"
        pluralRoute={routes.HARVESTS}
        loading={loading}
        searchRoute={''}
        bodyColumns={[]}
      />
    );
  }
  if (data.plantingGroup.plantings.length === 0) {
    return <PageError error={new Error('No plantings found')} />;
  }
  const plantingsData = data.plantingGroup.plantings;

  const tasks = plantingsData.flatMap((p) => p.tasks.edges.map((e) => e.node));
  const tasksGroupedWithStatus = [...groupTasksWithStatus(tasks).values()];
  const tasksView = (
    <Box sx={{ mt: 2 }}>
      <TitleCard
        title="Tasks"
        rightElement={<div />}
        loading={loading}
        content={
          <TaskGroupTable
            onCompleted={() => void refetch()}
            taskGroups={tasksGroupedWithStatus.map(({ tasks }) => tasks)}
            showActionButtons={true}
          />
        }
      />
    </Box>
  );

  const cropDataToAdd = data.plantingGroup.plantings[0].crop;
  const lostTraysTable =
    data.plantingGroup.lost_trays.length > 0 ? (
      <Box sx={{ mt: 2 }}>
        <TitleCard
          title="Lost Trays"
          rightElement={<div />}
          content={
            <LostTraysTable
              hidePlantingGroup
              lostTrays={data.plantingGroup.lost_trays.map((lt) => ({
                ...lt,
                crop: cropDataToAdd,
              }))}
            />
          }
        />
      </Box>
    ) : null;

  const singlePlantings = (
    <Box sx={{ mt: 2 }}>
      <ExpandoCard
        title="Order-Specific Plantings"
        rightElement={<div />}
        loading={loading}
        content={
          <PlantingTable
            useIndividualPlantingLinks
            plantings={plantingsData.map((p) => ({
              ...p,
              tasks: {
                count: p.tasks.edges.length,
              },
            }))}
          />
        }
      />
    </Box>
  );

  const allOrders = plantingsData
    .map((p) => (p.order == null ? null : { ...p.order, trays: Number.parseFloat(p.trays_rounded) }))
    .filter(utils.notNull);
  // There could be multiple plantings for the same order
  const ordersGrouped = allOrders.reduce(
    (acc, order) => {
      const existing = acc.get(order.id);
      if (existing == null) {
        acc.set(order.id, order);
      } else {
        acc.set(order.id, { ...existing, trays: utils.addTraysRaw(existing.trays, order.trays) });
      }
      return acc;
    },
    new Map<
      string,
      {
        id: string;
        order_status: string;
        customer: string;
        order_fulfillment_time: number;
        trays: number;
      }
    >(),
  );
  const allOrdersArray = [...ordersGrouped.values()];
  const sortedOrders = [...allOrdersArray];
  sortedOrders.sort((a, b) => compareOrders(a, b));
  const orders = (
    <Box sx={{ mt: 2 }}>
      <TitleCard
        title="Orders"
        rightElement={<div />}
        content={<OrderTable orders={sortedOrders} showTrays={true} />}
      />
    </Box>
  );

  const onePlanting = plantingsData[0];
  const updatedAt = Math.max(...plantingsData.map((p) => p.updated_at));
  const createdAt = Math.min(...plantingsData.map((p) => p.created_at));
  const yieldPerTrayOunces = utils.round(Number.parseFloat(onePlanting.yield_per_tray_ounces));
  const yieldPerTrayGrams = utils.round(
    utils.convert({
      quantity: Number.parseFloat(onePlanting.yield_per_tray_ounces),
      inputUnit: WEIGHT_UNITS.OUNCES,
      outputUnit: WEIGHT_UNITS.GRAMS,
    }),
    0,
  );
  const plantingsDataSurplus = plantingsData.filter((p) => p.planting_state === PLANTING_STATE.SURPLUS);
  const plantingsDataAllocated = plantingsData.filter((p) => p.planting_state !== PLANTING_STATE.SURPLUS);
  const expectedYieldOuncesSurplus = plantingsDataSurplus.reduce(
    (acc, p) =>
      utils.addTraysRaw(
        acc,
        utils.multiplyTraysRaw(Number.parseFloat(p.yield_per_tray_ounces), Number.parseFloat(p.trays_rounded)),
      ),
    0,
  );
  const expectedYieldOuncesSurplusRounded = utils.round(expectedYieldOuncesSurplus, 1);
  const expectedYieldGramsSurplus = utils.round(
    utils.convert({
      inputUnit: WEIGHT_UNITS.OUNCES,
      outputUnit: WEIGHT_UNITS.GRAMS,
      quantity: expectedYieldOuncesSurplus,
    }),
    0,
  );
  const expectedYieldOuncesAllocated = plantingsDataAllocated.reduce(
    (acc, p) =>
      utils.addTraysRaw(
        acc,
        utils.multiplyTraysRaw(Number.parseFloat(p.yield_per_tray_ounces), Number.parseFloat(p.trays_rounded)),
      ),
    0,
  );
  const expectedYieldGramsAllocated = utils.round(
    utils.convert({
      inputUnit: WEIGHT_UNITS.OUNCES,
      outputUnit: WEIGHT_UNITS.GRAMS,
      quantity: expectedYieldOuncesAllocated,
    }),
    0,
  );
  const expectedYieldOuncesRaw = utils.addTraysRaw(expectedYieldOuncesSurplus, expectedYieldOuncesAllocated);
  const expectedYieldOunces = utils.round(expectedYieldOuncesRaw, 1);
  const expectedYieldGrams = utils.round(
    utils.convert({
      inputUnit: WEIGHT_UNITS.OUNCES,
      outputUnit: WEIGHT_UNITS.GRAMS,
      quantity: expectedYieldOuncesRaw,
    }),
    0,
  );
  const traysForSurplus = plantingsDataSurplus.reduce(
    (acc, p) => utils.addTraysRaw(acc, Number.parseFloat(p.trays_rounded)),
    0,
  );
  const traysAllocated = plantingsDataAllocated.reduce(
    (acc, p) => utils.addTraysRaw(acc, Number.parseFloat(p.trays_rounded)),
    0,
  );
  const traysLost = data.plantingGroup.lost_trays.reduce((acc, p) => utils.addTraysRaw(acc, p.trays_lost), 0);
  const currentTrays = utils.addTraysRaw(traysAllocated, traysForSurplus);
  const yieldUnitIsGrams = onePlanting.crop?.yield_unit === WEIGHT_UNITS.GRAMS;
  const expectedYield =
    onePlanting.crop == null
      ? 'No crop found'
      : yieldUnitIsGrams
        ? `${expectedYieldGrams} grams`
        : `${expectedYieldOunces} ounces`;
  const expectedYieldPerTray =
    onePlanting.crop == null
      ? 'No crop found'
      : yieldUnitIsGrams
        ? `${yieldPerTrayGrams} grams`
        : `${yieldPerTrayOunces} ounces`;
  const surplusYield =
    onePlanting.crop == null
      ? 'No crop found'
      : yieldUnitIsGrams
        ? `${expectedYieldGramsSurplus} grams`
        : `${expectedYieldOuncesSurplusRounded} ounces`;
  const allocatedYield =
    onePlanting.crop == null
      ? 'No crop found'
      : yieldUnitIsGrams
        ? `${expectedYieldGramsAllocated} grams`
        : `${utils.round(expectedYieldOuncesAllocated, 1)} ounces`;
  const plantedAt =
    onePlanting.tasks.edges.map((e) => e.node).find((t) => isPlantStep(t.task_name))?.task_completed_time ?? undefined;

  const currentStage = getPlantingStage({
    plantingStatus: onePlanting.planting_status,
    tasks: onePlanting.tasks.edges.map((e) => e.node),
  });

  const maybeCrop = onePlanting.crop;
  const seedNeeded =
    maybeCrop != null && maybeCrop.sowing_density != null
      ? Decimal.mul(maybeCrop.sowing_density, currentTrays).toNumber()
      : undefined;
  const seedPerTray = maybeCrop?.sowing_density ?? undefined;
  const seedUnit = maybeCrop?.sow_density_unit ?? undefined;
  const shortenedSeedUnit = seedUnit ? shortenWeightUnit(seedUnit) : undefined;

  const columns: Array<[string, React.ReactElement | string] | [string, React.ReactElement | string, number]> = [
    [
      'Harvest',
      <HarvestLink key={1} id={getID(onePlanting.id)} title={formatTime(onePlanting.planting_time, { timezone })} />,
    ],
    [
      'Expires',
      onePlanting.crop
        ? formatTime(onePlanting.planting_time + (onePlanting.crop.max_hold_time ?? 0), { timezone })
        : '-',
    ],
    [
      'Crop',
      onePlanting.crop ? (
        <CropIdLink key={1} id={getID(onePlanting.crop.id)} name={onePlanting.crop.crop_name} />
      ) : (
        'No crop found'
      ),
    ],
    ['Status', formatStateOrStatus(onePlanting.planting_status)],
    ['Current Stage', currentStage || '-'],
    ['Assigned Trays', formatNumber(traysAllocated, language)],
    ['Surplus Trays', formatNumber(traysForSurplus, language)],
    ['Total Current Trays', formatNumber(currentTrays, language)],
    ['Lost Trays', formatNumber(traysLost, language)],
    [
      'Seed Needed',
      seedNeeded != null
        ? `${formatNumber(seedNeeded, language)} ${seedUnit ?? ''}${seedPerTray ? ` (${seedPerTray}${shortenedSeedUnit ?? ''} / tray)` : ''}`
        : 'Unknown',
    ],
    ['Expected Yield Per Tray', expectedYieldPerTray],
    ['Expected Total Yield', expectedYield],
    ['Allocated Yield', allocatedYield],
    ['Surplus Yield', surplusYield],
    [
      'Planted At',
      plantedAt ? (
        <Time key={1} time={plantedAt} options={{ time: true, removeDayOfWeek: true }} dontUseTimezone />
      ) : (
        '-'
      ),
    ],
    ['Notes', onePlanting.planting_notes || '-'],
    ['Updated At', <Time key={1} time={updatedAt} options={{ time: true, removeDayOfWeek: true }} dontUseTimezone />],
    ['Created At', <Time key={1} time={createdAt} options={{ time: true, removeDayOfWeek: true }} dontUseTimezone />],
  ];

  const plantingGroupOptions = (
    <PlantingGroupOptions
      hasBeenPlanted={plantedAt !== undefined}
      onSuccess={(shouldRefetchSelf) => {
        if (shouldRefetchSelf) {
          void refetch();
        }
      }}
      plantingGroupStatus={onePlanting.planting_status}
      plantingIds={plantingsData.map((p) => getNumericID(p.id))}
    />
  );

  return (
    <>
      <Helmet>
        <title>{`${
          onePlanting.crop?.crop_name
            ? `${onePlanting.crop.crop_name} - ${formatTime(onePlanting.planting_time, { timezone })}`
            : `${formatTime(onePlanting.planting_time, { timezone })}`
        } - Planting Group`}</title>
      </Helmet>
      <PageView
        title="Planting Group"
        name="Planting Group"
        pluralName="Plantings"
        pluralRoute={routes.PLANTINGS}
        searchRoute={routes.makePlantingSearch(getNumericID(onePlanting.id))}
        bodyColumns={columns}
        extra={
          <>
            {tasksView}
            {orders}
            {lostTraysTable}
            {singlePlantings}
          </>
        }
        headerRightElement={plantingGroupOptions}
        loading={loading}
      />
    </>
  );
}
